import { Stack, Box, Paper, Typography, Button, Card, CardMedia, CardContent, CardActionArea, Container, Grid } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { lightTheme, brand } from '../components/themes/main_theme.js';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import HandshakeIcon from '@mui/icons-material/Handshake';
import MailIcon from '@mui/icons-material/Mail';
import NotificationsIcon from '@mui/icons-material/Notifications';
import FavoriteIcon from '@mui/icons-material/Favorite';
import hero_image from '../images/hero-building.jpg';
import { useNewsletters, useMinistries, usePageContent } from '../data/cms.ts';
import { useNavigate } from 'react-router-dom';
import PhotoCarousel from '../components/PhotoCarousel.tsx';
import { ContactButtons } from './ContactUs.tsx';
import { useMediaQuery } from '@mui/material';

const CardStack = ({ image, description, url }: { image: string, description: string, url: string }) => {
  const navigate = useNavigate();
  const handleClick = () => {
    if (url === '/donate') navigate(url);
    else window.open(url, '_blank');
  };
  return (
    // Fills its grid cell so every card in the row is the same height, and the
    // image sits on top so a headline wrapping to two lines can no longer push
    // the photo down out of line with its neighbours.
    <CardActionArea
      onClick={handleClick}
      sx={{
        height: '100%',
        borderRadius: 5,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 },
      }}
    >
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          p: 1.5,
        }}
      >
        <CardMedia
          component="img"
          image={image}
          sx={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', borderRadius: 3 }}
        />
        {/* Grows into whatever height the tallest card in the row sets, so a
            one-line headline sits centred rather than stranded at the top. */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1.5, px: 0.5 }}>
          <Typography
            variant="h6"
            color="primary"
            align="center"
            sx={{ fontSize: '1.05rem', lineHeight: 1.35 }}
          >
            {description}
          </Typography>
        </Box>
      </Card>
    </CardActionArea>
  );
};

/**
 * Impact figures.
 *
 * Only entries with a value are rendered, and the strip hides itself entirely
 * below two of them — so the ministry can fill these in over time without the
 * live site ever showing empty placeholders. Nothing here is invented: the
 * ministry count comes from the CMS and the founding year is a known fact.
 *
 * TODO(ministry): add the real totals for casitas built, families served and
 * students taught, then they will appear automatically.
 */
const buildImpactStats = (ministryCount: number) =>
  [
    { value: '2012', label: 'Serving since' },
    { value: ministryCount ? String(ministryCount) : '', label: 'Active ministries' },
    { value: '', label: 'Casitas built' },
    { value: '', label: 'Families served' },
  ].filter((stat) => stat.value !== '');

/** Section heading used down the home page. */
const SectionHeading = ({ icon: Icon, children }: { icon: any; children: React.ReactNode }) => (
  <Container maxWidth="lg" sx={{ mt: { xs: 6, md: 9 }, mb: { xs: 2, md: 3 } }}>
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Icon sx={{ color: 'secondary.main', fontSize: 30 }} />
      <Typography variant="h3" color="primary">{children}</Typography>
    </Stack>
  </Container>
);

const Home = () => {
  const navigate = useNavigate();
  const { items: newsletters } = useNewsletters();
  const { items: ministries } = useMinistries();
  const impactStats = buildImpactStats(ministries.length);

  // Editable copy. Any key the ministry has not filled in falls back to the
  // wording below, so an empty PageContent table changes nothing.
  const content = usePageContent('HOME');
  const text = (key: string, fallback: string) => content[key] || fallback;
  return (<>
      {/* Photo-led hero. The scrim runs bottom-up on phones (text sits under the
          image) and left-to-right on desktop, keeping white text well clear of
          the 4.5:1 contrast floor over any part of the photograph. */}
      <Box
        component="section"
        sx={{
          position: 'relative',
          minHeight: { xs: 540, md: 660 },
          display: 'flex',
          alignItems: 'center',
          backgroundImage: `url(${hero_image})`,
          backgroundSize: 'cover',
          backgroundPosition: { xs: 'center 35%', md: 'center 40%' },
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: {
              xs: 'linear-gradient(to top, rgba(42,26,20,0.94) 15%, rgba(42,26,20,0.70) 55%, rgba(42,26,20,0.40) 100%)',
              md: 'linear-gradient(to right, rgba(42,26,20,0.94) 0%, rgba(42,26,20,0.78) 42%, rgba(42,26,20,0.20) 100%)',
            },
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: { xs: 7, md: 9 } }}>
          <Box sx={{ maxWidth: { xs: '100%', md: 640 } }}>
            <Typography variant="overline" sx={{ color: brand.goldGlow, display: 'block', mb: 1.5 }}>
              Salt and Light Ministries · Guatemala
            </Typography>
            <Typography variant="h1" sx={{ color: '#FFFFFF', mb: 2.5 }}>
              {text('heroTitle', 'Be the Light')}
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.92)',
                fontSize: { xs: '1rem', md: '1.125rem' },
                lineHeight: 1.7,
                mb: 4,
                maxWidth: 560,
              }}
            >
              {text(
                'heroScripture',
                '“Let your light shine in front of men. Then they will see the good things you do and will honor your Father Who is in heaven.”'
              )}
              <Box component="span" sx={{ display: 'block', mt: 1, color: brand.goldGlow, fontWeight: 600 }}>
                Matthew 5:14–16
              </Box>
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                startIcon={<FavoriteIcon />}
                onClick={() => navigate('/donate')}
              >
                Give today
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/ministries')}
                sx={{
                  color: '#FFFFFF',
                  borderColor: 'rgba(255,255,255,0.65)',
                  '&:hover': { borderColor: '#FFFFFF', backgroundColor: 'rgba(255,255,255,0.10)' },
                }}
              >
                See our work
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Impact strip — gives visitors a sense of scale straight after the hero. */}
      {impactStats.length >= 2 && (
        <Box sx={{ bgcolor: 'primary.main', color: '#FFFFFF', py: { xs: 4, md: 5 } }}>
          <Container maxWidth="lg">
            <Grid container spacing={{ xs: 3, md: 2 }} justifyContent="center">
              {impactStats.map((stat) => (
                <Grid item xs={6} md={12 / impactStats.length} key={stat.label} sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" sx={{ color: brand.goldGlow, mb: 0.5 }}>
                    {stat.value}
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', letterSpacing: '0.04em', opacity: 0.92 }}>
                    {stat.label}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      {/* Stacks on phones — previously this was a plain flex row, so the two
          panels were squeezed side by side on a narrow screen. */}
      <Container maxWidth="lg" sx={{ mt: { xs: 5, md: 8 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 2.5, md: 3 }} alignItems="stretch">
          <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, flex: 1, border: `1px solid ${brand.hairline}` }}>
            <Typography variant="overline" color="secondary">Our Mission</Typography>
            <Typography variant="h4" color="primary" sx={{ mt: 0.5, mb: 1.5 }}>
              {text('missionHeading', "Salt and light where we're sent")}
            </Typography>
            <Typography color="text.secondary">
              {text(
                'missionStatement',
                'To be the salt and light of the communities we reach so they can see our good works and glorify our father in Heaven'
              )}
            </Typography>
          </Paper>
          <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, flex: 1, border: `1px solid ${brand.hairline}` }}>
            <Typography variant="overline" color="secondary">Our Vision</Typography>
            <Typography variant="h4" color="primary" sx={{ mt: 0.5, mb: 1.5 }}>
              {text('visionHeading', 'Meeting every need, body and soul')}
            </Typography>
            <Typography color="text.secondary">
              {text(
                'visionStatement',
                'Be able to help supply the material, physical, mental, and -most importantly- spiritual needs of the people of Guatemala through different projects and to help train the people here to do so as well'
              )}
            </Typography>
          </Paper>
        </Stack>
      </Container>

      <Box display="flex" justifyContent="center" mt={{ xs: 4, md: 5 }}>
        <Button variant="contained" color="secondary" size="large" startIcon={<FavoriteIcon />} onClick={() => navigate('/donate')}>
          Join us in the work
        </Button>
      </Box>

      <SectionHeading icon={NewspaperIcon}>Latest news</SectionHeading>
      {/* Same Container as the heading above — previously the cards sat in a
          full-width centred box and did not line up with the section title. */}
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 2, md: 3 }} alignItems="stretch">
          {newsletters.slice(0, 4).map((item, idx) => (
            <Grid item xs={12} sm={6} md={3} key={idx} sx={{ display: 'flex' }}>
              <CardStack url={item.url} description={item.description} image={item.image} />
            </Grid>
          ))}
        </Grid>
        <Box display="flex" justifyContent="center" mt={4}>
          <Button
            variant="outlined"
            startIcon={<NotificationsIcon />}
            onClick={() => window.open('http://eepurl.com/ilnCGv', '_blank', 'noopener,noreferrer')}
          >
            Subscribe to updates
          </Button>
        </Box>
      </Container>

      <SectionHeading icon={HandshakeIcon}>Our ministries</SectionHeading>
      <Container maxWidth="lg">
        <PhotoCarousel />
      </Container>
      <SectionHeading icon={MailIcon}>Get in touch</SectionHeading>
      <ContactButtons />
      </>
  );
};

export default Home;